import { Project, Story, Analytics, User, TemplateScene } from "@/types/cmsTypes";
import projectHistorical from "@/assets/project-historical.jpg";
// import projectNature from "@/assets/project-nature.jpg";
// import projectUrban from "@/assets/project-urban.jpg";

//Push
export const mockUser: User = {
  id: "user-1",
  name: "Sarah Chen",
  email: "sarah@lureo.com",
  avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b300?w=150&h=150&fit=crop&crop=face",
  role: "creator"
};

export const mockProjects: Project[] = [
  {
    id: "proj-1",
    title: "Historic Castle Walking Tour",
    description: "Discover the hidden stories of Warwick Castle through an immersive AR experience featuring medieval tales and architectural insights.",
    heroImage: projectHistorical,
    duration: 45,
    tags: ["History", "Architecture", "Medieval", "Educational"],
    status: "live",
    locationInfo: { latitude: 52.2789, longitude: -1.5850, address: "Warwick Castle, UK" },
    createdAt: new Date("2024-01-15"),
    updatedAt: new Date("2024-01-18"),
    storiesCount: 8,
    views: 2847,
    completionRate: 78
  },
  {
    id: "proj-2", 
    title: "Enchanted Forest Nature Walk",
    description: "Experience the magic of ancient woodlands with AR wildlife encounters and environmental storytelling through pristine forest paths.",
    heroImage: projectHistorical,
    duration: 30,
    tags: ["Nature", "Wildlife", "Environment", "Family"],
    status: "live",
    locationInfo: { latitude: 51.2867, longitude: -0.6577, address: "Windsor Great Park, UK" },
    createdAt: new Date("2024-01-10"),
    updatedAt: new Date("2024-01-20"),
    storiesCount: 6,
    views: 1523,
    completionRate: 85
  },
  {
    id: "proj-3",
    title: "Urban Street Art Discovery",
    description: "Explore the vibrant street art scene with interactive AR overlays revealing artist stories, techniques, and cultural impact in the city.",
    heroImage: projectHistorical,
    duration: 60,
    tags: ["Art", "Urban", "Culture", "Contemporary"],
    status: "draft",
    locationInfo: { latitude: 51.5142, longitude: -0.0931, address: "Shoreditch, London, UK" },
    createdAt: new Date("2024-01-22"),
    updatedAt: new Date("2024-01-25"),
    storiesCount: 4,
    views: 0,
    completionRate: 0
  }
];

export const mockStories: Story[] = [
  // Historic Castle Walking Tour Stories (proj-1)
  {
    id: "story-1",
    projectId: "proj-1",
    title: "The Great Hall",
    description: "Step into the magnificent Great Hall where medieval feasts once took place. Marvel at the soaring wooden beams, intricate stone carvings, and imagine the sounds of royal banquets echoing through these ancient walls. Discover the architectural marvels and royal ceremonies that shaped centuries of history.",
    image: "/src/assets/great-hall-interior.jpg", // Medieval hall
    locationInfo: {
      address: "Warwick Castle, Warwick, UK",
      coordinates: { lat: 52.2791, lng: -1.5851 }
    },
    media: { type: "AR", url: "/audio/great-hall.mp3" },
    order: 1,
    status: "live",
    version: "1.2",
    createdAt: new Date("2024-12-15"),
    updatedAt: new Date("2024-12-20")
  },
  {
    id: "story-2",
    projectId: "proj-1", 
    title: "The Ramparts",
    description: "Walk along the ancient defensive walls that protected the castle for over 800 years. Learn about medieval warfare strategies, siege techniques, and the daily lives of castle guards. Enjoy breathtaking panoramic views of the surrounding countryside that once served as an early warning system against approaching enemies.",
    image: "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=800&h=600&fit=crop", // Castle ramparts
    locationInfo: {
      address: "Warwick Castle Ramparts, Warwick, UK",
      coordinates: { lat: 52.2801, lng: -1.5841 }
    },
    media: { type: "360 Video", url: "/video/ramparts.mp4" },
    order: 2,
    status: "live",
    version: "1.1",
    createdAt: new Date("2024-12-16"),
    updatedAt: new Date("2024-12-18")
  },
  {
    id: "story-3",
    projectId: "proj-1",
    title: "The Royal Chambers",
    description: "Enter the private quarters where kings and queens once resided in opulent splendor. Experience the luxury and intrigue of royal court life through immersive AR recreations. See the elaborate tapestries, ornate furniture, and secret passages that tell tales of romance, betrayal, and political machinations.",
    image: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=600&fit=crop", // Royal chambers
    locationInfo: {
      address: "Warwick Castle Royal Chambers, Warwick, UK",
      coordinates: { lat: 52.2785, lng: -1.5855 }
    },
    media: { type: "Audio Only", url: "/audio/royal-chambers.mp3" },
    order: 3,
    status: "live",
    version: "1.0",
    createdAt: new Date("2024-12-17"),
    updatedAt: new Date("2024-12-19")
  },
  {
    id: "story-4",
    projectId: "proj-1",
    title: "The Castle Keep",
    description: "Ascend the mighty keep, the heart of the castle's defenses and symbol of royal power. Learn about siege warfare, medieval engineering, and how this massive stone tower withstood countless attacks over the centuries. Discover the strategic importance of height in medieval castle design.",
    image: "https://images.unsplash.com/photo-1582650625119-3a31f8fa2699?w=800&h=600&fit=crop", // Castle keep tower
    locationInfo: {
      address: "Warwick Castle Keep, Warwick, UK",
      coordinates: { lat: 52.2795, lng: -1.5845 }
    },
    media: { type: "AR", url: "/video/castle-keep.mp4" },
    order: 4,
    status: "live",
    version: "1.3",
    createdAt: new Date("2024-12-18"),
    updatedAt: new Date("2024-12-21")
  },
  {
    id: "story-5",
    projectId: "proj-1",
    title: "The Dungeon",
    description: "Descend into the dark, damp dungeons where prisoners were held in medieval times. Discover tales of famous captives, escape attempts, and the harsh realities of medieval justice. Learn about the different types of punishments and how the castle's underground chambers served multiple purposes.",
    image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop", // Dark dungeon
    locationInfo: {
      address: "Warwick Castle Dungeon, Warwick, UK",
      coordinates: { lat: 52.2788, lng: -1.5848 }
    },
    media: { type: "AR", url: "/audio/dungeon.mp3" },
    order: 5,
    status: "live",
    version: "1.0",
    createdAt: new Date("2024-12-19"),
    updatedAt: new Date("2024-12-22")
  },
  {
    id: "story-6",
    projectId: "proj-1",
    title: "The Chapel",
    description: "Visit the sacred chapel where royal weddings, coronations, and daily prayers took place. Experience the spiritual heart of the castle through beautiful stained glass windows, ornate altarpieces, and ancient stone carvings. Learn about the role of religion in medieval castle life and royal ceremonies.",
    image: "https://images.unsplash.com/photo-1576675784201-0e142b423952?w=800&h=600&fit=crop", // Gothic chapel
    locationInfo: {
      address: "Warwick Castle Chapel, Warwick, UK",
      coordinates: { lat: 52.2792, lng: -1.5852 }
    },
    media: { type: "Audio Only", url: "/audio/chapel.mp3" },
    order: 6,
    status: "live",
    version: "1.1",
    createdAt: new Date("2024-01-20"),
    updatedAt: new Date("2024-01-23")
  },
  {
    id: "story-7",
    projectId: "proj-1",
    title: "The Kitchen",
    description: "Explore the massive medieval kitchen that fed hundreds of castle inhabitants daily. See the enormous fireplaces, meat hooks, and preparation areas where elaborate feasts were created. Learn about medieval cooking methods, food preservation, and the complex hierarchy of castle staff.",
    image: "https://images.unsplash.com/photo-1574484284002-952d92456975?w=800&h=600&fit=crop", // Medieval kitchen
    locationInfo: {
      address: "Warwick Castle Kitchen, Warwick, UK",
      coordinates: { lat: 52.2784, lng: -1.5849 }
    },
    media: { type: "AR", url: "/video/kitchen.mp4" },
    order: 7,
    status: "live",
    version: "1.0",
    createdAt: new Date("2024-01-21"),
    updatedAt: new Date("2024-01-24")
  },
  {
    id: "story-8",
    projectId: "proj-1",
    title: "The Courtyard",
    description: "Stand in the central courtyard where tournaments were held and daily castle life unfolded. Witness the bustling activity of medieval times come to life around you - from blacksmiths and merchants to knights training for battle. This was the social and economic heart of the castle community.",
    image: "https://images.unsplash.com/photo-1598301257942-57c4dba79516?w=800&h=600&fit=crop", // Castle courtyard
    locationInfo: {
      address: "Warwick Castle Courtyard, Warwick, UK",
      coordinates: { lat: 52.2790, lng: -1.5850 }
    },
    media: { type: "AR", url: "/audio/courtyard.mp3" },
    order: 8,
    status: "live",
    version: "1.2",
    createdAt: new Date("2024-01-22"),
    updatedAt: new Date("2024-01-25")
  },
  // Nature Forest Walk Stories (proj-2)
  {
    id: "story-9",
    projectId: "proj-2",
    title: "Ancient Oak Grove",
    description: "Enter the mystical grove where ancient oaks have stood for centuries. Listen to the whispers of nature and feel the magic.",
    image: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=800&h=600&fit=crop", // Ancient oak trees
    locationInfo: {
      address: "Richmond Park, London, UK",
      coordinates: { lat: 51.4994, lng: -0.1246 }
    },
    media: { type: "AR", url: "/audio/oak-grove.mp3" },
    order: 1,
    status: "live",
    version: "2.0",
    createdAt: new Date("2024-01-17"),
    updatedAt: new Date("2024-01-20")
  },
  {
    id: "story-10",
    projectId: "proj-2",
    title: "Fairy Ring",
    description: "Discover the legendary fairy ring hidden deep in the forest. Ancient folklore speaks of magical encounters here.",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop", // Forest mushroom ring
    locationInfo: {
      address: "Richmond Park Fairy Ring, London, UK",
      coordinates: { lat: 51.5004, lng: -0.1256 }
    },
    order: 2,
    status: "draft",
    version: "1.0",
    createdAt: new Date("2024-01-18"),
    updatedAt: new Date("2024-01-21")
  },
  // Urban Street Art Stories (proj-3)
  {
    id: "story-11",
    projectId: "proj-3",
    title: "Street Art Corner",
    description: "Explore vibrant street art that tells the story of urban culture and community expression in this dynamic neighborhood.",
    image: "https://images.unsplash.com/photo-1499781350541-7783f6c6a0c8?w=800&h=600&fit=crop", // Colorful street art
    locationInfo: {
      address: "Shoreditch, London, UK",
      coordinates: { lat: 51.5144, lng: -0.1419 }
    },
    order: 1,
    status: "draft",
    version: "1.0",
    createdAt: new Date("2024-01-19"),
    updatedAt: new Date("2024-01-22")
  }
];

export const mockTemplateScenes: TemplateScene[] = [
  {
    id: "template-1",
    title: "Historic Landmark Tour",
    description: "A comprehensive template for creating immersive historical walking tours with AR overlays and location-based storytelling. Perfect for heritage sites, monuments, and cultural landmarks where visitors want to learn about the past while exploring the present. The template includes pre-configured scenes for key historical moments, interactive timelines, and character narrations that bring history to life. Ideal for museums, tourism boards, and educational institutions looking to create engaging historical experiences.",
    heroImage: projectHistorical,
    category: "History",
    tags: ["Historical", "Walking Tour", "AR", "Educational"],
    duration: 45,
    difficulty: "beginner",
    locationInfo: {
      latitude: 51.5074,
      longitude: -0.1278,
      address: "London, UK"
    }
  },
  {
    id: "template-2",
    title: "Nature Trail Experience",
    description: "Explore natural environments with wildlife encounters, environmental education, and scenic route guidance. This template helps you create immersive outdoor experiences that connect visitors with nature through AR-enhanced storytelling. Features include animal identification guides, plant information overlays, and interactive conservation messages. Perfect for national parks, nature reserves, and outdoor education programs seeking to enhance visitor engagement with the natural world.",
    heroImage: projectHistorical,
    category: "Nature",
    tags: ["Nature", "Wildlife", "Outdoor", "Family"],
    duration: 30,
    difficulty: "beginner",
    locationInfo: {
      latitude: 51.2867,
      longitude: -0.6577,
      address: "Windsor Great Park, UK"
    }
  },
  {
    id: "template-3",
    title: "Urban Art Discovery",
    description: "Discover street art and urban culture with interactive AR overlays revealing artist stories and techniques. This template transforms city streets into an open-air gallery where visitors can learn about the artists, their inspirations, and the cultural significance of each piece. Includes features for showcasing art evolution over time, behind-the-scenes creation processes, and community impact stories. Ideal for art districts, cultural organizations, and city tourism initiatives looking to celebrate local creative communities.",
    heroImage: projectHistorical,
    category: "Art & Culture",
    tags: ["Art", "Urban", "Culture", "Contemporary"],
    duration: 60,
    difficulty: "intermediate",
    locationInfo: {
      latitude: 51.5142,
      longitude: -0.0931,
      address: "Shoreditch, London, UK"
    }
  },
  {
    id: "template-4",
    title: "Museum Gallery Walk",
    description: "Create engaging museum experiences with detailed artifact information, historical context, and interactive exhibits. This template helps museums enhance visitor engagement by providing rich multimedia content, 3D reconstructions, and immersive storytelling around collections. Features include curator commentary, artifact provenance stories, and interactive timelines that connect objects to broader historical narratives. Perfect for art museums, history museums, and cultural institutions seeking to modernize their visitor experience and make collections more accessible.",
    heroImage: projectHistorical,
    category: "Museum",
    tags: ["Museum", "Art", "Education", "Indoor"],
    duration: 90,
    difficulty: "intermediate",
    locationInfo: {
      latitude: 51.5194,
      longitude: -0.1270,
      address: "British Museum, London, UK"
    }
  },
  {
    id: "template-5",
    title: "Architectural Heritage",
    description: "Explore architectural marvels with detailed building histories, design insights, and structural analysis. This template enables you to showcase the evolution of architectural styles, construction techniques, and the stories behind iconic structures. Features include 3D building reconstructions, architect biographies, and interactive design element explanations. Ideal for architectural tours, heritage organizations, and educational programs focused on design, engineering, and urban planning.",
    heroImage: projectHistorical,
    category: "Architecture",
    tags: ["Architecture", "Design", "Heritage", "Technical"],
    duration: 75,
    difficulty: "advanced",
    locationInfo: {
      latitude: 51.4994,
      longitude: -0.1246,
      address: "Westminster, London, UK"
    }
  },
  {
    id: "template-6",
    title: "Culinary Journey",
    description: "Food and culture exploration template featuring local cuisine, restaurant histories, and culinary traditions. This template helps you create immersive food tours that tell the stories behind dishes, chefs, and dining establishments. Features include recipe demonstrations, ingredient sourcing stories, and cultural context about how food traditions evolved. Perfect for food tours, culinary schools, and tourism organizations looking to celebrate local food culture and connect visitors with authentic dining experiences.",
    heroImage: projectHistorical,
    category: "Food & Culture",
    tags: ["Food", "Culture", "Restaurants", "Local"],
    duration: 50,
    difficulty: "beginner",
    locationInfo: {
      latitude: 51.5155,
      longitude: -0.0922,
      address: "Borough Market, London, UK"
    }
  }
];

export const mockAnalytics: Analytics = {
  totalViews: 4370,
  totalProjects: 3,
  totalStories: 18,
  avgCompletionRate: 81,
  topProjects: mockProjects.slice(0, 3),
  recentActivity: [
    {
      id: "activity-1",
      action: "updated",
      resource: "story",
      resourceId: "story-1",
      resourceTitle: "The Great Hall",
      timestamp: new Date("2024-01-25T10:30:00")
    },
    {
      id: "activity-2", 
      action: "created",
      resource: "project",
      resourceId: "proj-3",
      resourceTitle: "Urban Street Art Discovery",
      timestamp: new Date("2024-01-22T14:15:00")
    },
    {
      id: "activity-3",
      action: "published",
      resource: "story",
      resourceId: "story-2",
      resourceTitle: "The Ramparts",
      timestamp: new Date("2024-01-20T09:45:00")
    }
  ]
};